/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useGetBusinessData, useVenueImageData } from "../../Utility";

import "./BusinessUsersPage.css";
import {
  onBusinessUpdate,
  onBusinessUpdateReset,
  onGetBusinesses,
} from "../../Store/Slices/businessSlice";
import {
  onGetImages,
  onVenueSingleImageUpload,
  onVenueSingleImageUploadReset,
} from "../../Store/Slices/venueImageSlice";
import toast from "react-hot-toast";
import CommonTable from "../Components/CommonTable";
import { capsFirstLetter } from "../../utils/UtilityFunctions";
import { AmenitiesChipInput } from "../../enduser/pages/profile/BusinessCompletionProfile/AmenitiesChipInput";
import { APP_CONFIG } from "../../config/appData";

function getEmptyEditForm() {
  return {
    business_name: "",
    email: "",
    phone: "",
    city: "",
    price_min: "",
    price_max: "",
    status: "pending",
    profile_step: 1,
    is_visible: false,
    top_rank: "",
    apply_offers: false,
    tags: "",
    venue_type: "",
    capacity_min: "",
    capacity_max: "",
    area_sqft: "",
    number_of_halls: "",
    full_address: "",
    venue_city: "",
    state: "",
    pincode: "",
    description: "",
    amenities: "",
    vendor_type: "",
    willing_to_travel: "",
    years_of_experience: "",
    service_radius_km: "",
  };
}

function BusinessUsersPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const businessData = useGetBusinessData();
  const list = businessData?.businessesList ?? [];
  const loading = businessData?.getBusinessesLoading ?? false;
  const approving = businessData?.isLoading ?? false;
  const approve = useRef(false);
  const reject = useRef(false);
  const [approveModal, setApproveModal] = useState(null);
  const [tagModal, setTagModal] = useState(null);
  const [tagDraft, setTagDraft] = useState("");
  const [rankDraft, setRankDraft] = useState({});
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(getEmptyEditForm());
  const initialEditFormRef = useRef(null);
  const imagesForBusinessIdRef = useRef(null);
  const editImageInputRef = useRef(null);
  const imageData = useVenueImageData();

  const parseTags = (val) => {
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === "string" && val.trim())
      return val.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };
  useEffect(() => {
    dispatch(onGetBusinesses({ all: true, limit: 50, offset: 0 }));
  }, []);

  const addTagToDraft = (tag) => {
    const current = parseTags(tagDraft);
    if (current.includes(tag)) return;
    setTagDraft([...current, tag].join(", "));
  };

  const removeTagFromDraft = (tagToRemove) => {
    const current = parseTags(tagDraft).filter((t) => t !== tagToRemove);
    setTagDraft(current.join(", "));
  };

  const clearAllTags = () => {
    setTagDraft("");
  };

  const openApproveModalHandler = (row) => {
    setApproveModal({
      id: row.id ?? row.business_id,
      name: row.business_name ?? row.name ?? row.email ?? `#${row.id}`,
      business_type: row.business_type,
    });
  };

  const closeApproveModal = () => {
    if (!approving) setApproveModal(null);
  };

  const handleConfirmApprove = (Reject) => {
    if (!approveModal?.id) return;
    dispatch(
      onBusinessUpdate({
        id: approveModal.id,
        is_visible: Reject ? false : true,
        status: Reject ? "rejected" : "approved",
        business_type: approveModal.business_type,
      }),
    );
    if (Reject) {
      reject.current = true;
      approve.current = false;
    } else {
      approve.current = true;
      reject.current = false;
    }
  };

  useEffect(() => {
    if (businessData?.status_code === "200") {
      dispatch(onBusinessUpdateReset());
      toast.success(businessData?.message);
      setApproveModal(null);
      setTagModal(null);
      setEditModal(null);
      setTagDraft("");
      setRankDraft({});
      setEditForm(getEmptyEditForm());
      initialEditFormRef.current = null;
      approve.current = false;
      reject.current = false;
      dispatch(onGetBusinesses({ all: true, limit: 50, offset: 0 }));
    }
  }, [businessData]);

  const openTagModal = (row) => {
    const existing = row.tags;
    setTagModal(row);
    setTagDraft(
      Array.isArray(existing)
        ? existing.join(", ")
        : typeof existing === "string"
          ? existing
          : ""
    );
  };

  const closeTagModal = () => {
    if (!approving) {
      setTagModal(null);
      setTagDraft("");
    }
  };

  const openEditModal = (row) => {
    const v = row.venue;
    const vendor = row.vendor;
    const existingTags = row.tags;
    const initialForm = {
      ...getEmptyEditForm(),
      business_name: row.business_name ?? "",
      email: row.email ?? "",
      phone: row.phone ?? "",
      city: row.city ?? "",
      price_min: row.price_min != null ? String(row.price_min) : "",
      price_max: row.price_max != null ? String(row.price_max) : "",
      status: row.status || "pending",
      profile_step: row.profile_step != null ? Number(row.profile_step) : 1,
      is_visible: row.is_visible ?? false,
      top_rank: row.top_rank != null && row.top_rank !== "" ? String(row.top_rank) : "",
      apply_offers: row.apply_offers ?? false,
      tags: Array.isArray(existingTags)
        ? existingTags.join(", ")
        : typeof existingTags === "string"
          ? existingTags
          : "",
      venue_type: v?.venue_type ?? "",
      capacity_min: v?.capacity_min != null ? String(v.capacity_min) : "",
      capacity_max: v?.capacity_max != null ? String(v.capacity_max) : "",
      area_sqft: v?.area_sqft != null ? String(v.area_sqft) : "",
      number_of_halls: v?.number_of_halls != null ? String(v.number_of_halls) : "",
      full_address: v?.full_address ?? vendor?.full_address ?? "",
      venue_city: v?.city ?? vendor?.city ?? "",
      state: v?.state ?? vendor?.state ?? "",
      pincode: v?.pincode ?? vendor?.pincode ?? "",
      description: v?.description ?? vendor?.description ?? "",
      amenities: v?.amenities ?? vendor?.amenities ?? "",
      vendor_type: vendor?.vendor_type ?? "",
      willing_to_travel: vendor?.willing_to_travel ?? "",
      years_of_experience: vendor?.years_of_experience != null ? String(vendor.years_of_experience) : "",
      service_radius_km: vendor?.service_radius_km != null ? String(vendor.service_radius_km) : "",
    };
    setEditModal(row);
    setEditForm(initialForm);
    initialEditFormRef.current = JSON.stringify(initialForm);
    imagesForBusinessIdRef.current = row.id ?? row.business_id;
    dispatch(onGetImages({ businessId: row.id ?? row.business_id }));
  };

  const closeEditModal = () => {
    if (!approving) {
      setEditModal(null);
      setEditForm(getEmptyEditForm());
      initialEditFormRef.current = null;
      imagesForBusinessIdRef.current = null;
      dispatch(onVenueSingleImageUploadReset());
    }
  };

  const isEditFormChanged = () => {
    if (!editModal || initialEditFormRef.current == null) return false;
    return JSON.stringify(editForm) !== initialEditFormRef.current;
  };

  // Refetch images after upload/delete when in edit modal
  const prevSingleImageLoading = useRef(imageData?.singleImageLoading);
  useEffect(() => {
    const wasLoading = prevSingleImageLoading.current;
    const isLoading = imageData?.singleImageLoading;
    prevSingleImageLoading.current = isLoading;
    if (editModal?.id && wasLoading && !isLoading) {
      dispatch(onGetImages({ businessId: editModal.id ?? editModal.business_id }));
      dispatch(onVenueSingleImageUploadReset());
    }
  }, [imageData?.singleImageLoading, editModal?.id, dispatch]);

  const editModalImages = () => {
    if (!editModal) return [];
    const bid = editModal.id ?? editModal.business_id;
    const isForThisBusiness = imagesForBusinessIdRef.current === bid;
    const loaded = !imageData?.getImagesLoading && Array.isArray(imageData?.getImagesData);
    if (isForThisBusiness && loaded) return imageData.getImagesData;
    return Array.isArray(editModal.images) ? editModal.images : [];
  };

  const handleDeleteImage = (imageId) => {
    if (!editModal?.id || !imageId) return;
    const businessId = editModal.id ?? editModal.business_id;
    dispatch(
      onVenueSingleImageUpload({
        businessId,
        imageId,
        isDeleted: true,
      })
    );
  };

  const handleAddImage = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !editModal?.id) return;
    e.target.value = "";
    const businessId = editModal.id ?? editModal.business_id;
    const images = editModalImages();
    const nextGalleryIndex = images.filter((i) => (i.image_type || i.type) === "gallery").length;
    dispatch(
      onVenueSingleImageUpload({
        businessId,
        file,
        type: "gallery",
        galleryIndex: nextGalleryIndex,
      })
    );
  };

  const setEditFormField = (field, value) => {
    setEditForm((p) => ({ ...p, [field]: value }));
  };

  const normalizeCity = (value) => String(value ?? "").trim().toLowerCase();

  const handleEditSave = () => {
    if (!editModal?.id) return;
    const id = editModal.id ?? editModal.business_id;
    const tagsVal = String(editForm.tags ?? "").trim();
    const topRankVal = editForm.top_rank === "" ? null : parseInt(editForm.top_rank, 10);
    if (editForm.top_rank !== "" && (Number.isNaN(topRankVal) || topRankVal < 1)) {
      toast.error("Top rank must be a positive integer or empty.");
      return;
    }
    if (topRankVal != null) {
      const currentCity = normalizeCity(editForm.city || editModal.city);
      const sameCityRankTaken = list.some((b) => {
        if ((b.status || "").toLowerCase() !== "approved") return false;
        if (String(b.id) === String(id)) return false;
        if (normalizeCity(b.city) !== currentCity) return false;
        if (b.top_rank == null || b.top_rank === "") return false;
        return Number(b.top_rank) === topRankVal;
      });
      if (sameCityRankTaken) {
        toast.error("This rank is already in use for this city.");
        return;
      }
    }
    const isVenue = editModal.business_type === "venue";
    const payload = {
      id,
      business_type: editModal.business_type,
      business_name: editForm.business_name?.trim() || undefined,
      email: editForm.email?.trim() || undefined,
      phone: editForm.phone?.trim() || undefined,
      city: editForm.city?.trim() || undefined,
      price_min: editForm.price_min !== "" ? Number(editForm.price_min) : undefined,
      price_max: editForm.price_max !== "" ? Number(editForm.price_max) : undefined,
      status: editForm.status || undefined,
      profile_step: editForm.profile_step,
      is_visible: editForm.is_visible,
      tags: tagsVal || undefined,
      top_rank: topRankVal,
      apply_offers: editForm.apply_offers,
    };
    if (isVenue && editModal.venue) {
      payload.venue = {
        id: editModal.venue.id,
        business_id: id,
        venue_type: editForm.venue_type?.trim() || undefined,
        capacity_min: editForm.capacity_min !== "" ? Number(editForm.capacity_min) : undefined,
        capacity_max: editForm.capacity_max !== "" ? Number(editForm.capacity_max) : undefined,
        area_sqft: editForm.area_sqft !== "" ? Number(editForm.area_sqft) : undefined,
        number_of_halls: editForm.number_of_halls !== "" ? Number(editForm.number_of_halls) : undefined,
        full_address: editForm.full_address?.trim() || undefined,
        city: editForm.venue_city?.trim() || undefined,
        state: editForm.state?.trim() || undefined,
        pincode: editForm.pincode?.trim() || undefined,
        description: editForm.description?.trim() || undefined,
        amenities: editForm.amenities?.trim() || undefined,
      };
    } else if (!isVenue) {
      const vid = editModal.vendor?.id ?? editModal.vendor?.vendor_id;
      payload.vendor = {
        ...(vid != null && { id: vid }),
        business_id: id,
        full_address: editForm.full_address?.trim() || undefined,
        city: editForm.venue_city?.trim() || undefined,
        state: editForm.state?.trim() || undefined,
        pincode: editForm.pincode?.trim() || undefined,
        description: editForm.description?.trim() || undefined,
        amenities: editForm.amenities?.trim() || undefined,
        vendor_type: editForm.vendor_type?.trim() || undefined,
        willing_to_travel: editForm.willing_to_travel?.trim() || undefined,
        years_of_experience: editForm.years_of_experience !== "" ? Number(editForm.years_of_experience) : undefined,
        service_radius_km: editForm.service_radius_km !== "" ? Number(editForm.service_radius_km) : undefined,
      };
    }
    dispatch(onBusinessUpdate(payload));
    setEditModal(null);
    setEditForm(getEmptyEditForm());
    initialEditFormRef.current = null;
  };

  const handleTagSave = () => {
    if (!tagModal?.id) return;
    const tags = String(tagDraft ?? "").trim();
    dispatch(
      onBusinessUpdate({
        id: tagModal.id ?? tagModal.business_id,
        business_type: tagModal.business_type,
        tags: tags || undefined,
      })
    );
    setTagModal(null);
    setTagDraft("");
  };

  const TAG_SUGGESTIONS = [
    "Premium",
    "Verified",
    "Featured",
    "Top Rated",
    "Best Value",
    "Luxury",
    "Budget Friendly",
    "New",
  ];

  const columns = [
    {
      key: "business_name",
      title: "Business Name",
      dataIndex: "business_name",
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
    },
    {
      key: "business_typw",
      title: "Business Type",
      dataIndex: "business_type",
      render: (row) => {
        return row?.business_type ? capsFirstLetter(row?.business_type) : "—";
      },
    },
    {
      key: "City",
      title: "City",
      dataIndex: "city",
    },
    {
      key: "registration_date",
      title: "Registration Date",
      render: (row) =>
        row.created_at
          ? new Date(row.created_at).toLocaleDateString("en-IN")
          : "—",
    },
    {
      key: "contact",
      title: "Contact / Email",
      render: (row) => row.email ?? row.phone ?? "—",
    },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span
          className={`admin-table-status admin-table-status-${(row.status || "pending").toLowerCase()}`}
        >
          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : "—"}
        </span>
      ),
    },
    {
      key: "tags",
      title: "Tags",
      render: (row) => {
        const tagsArr = parseTags(row.tags);
        const isApproved = (row.status || "").toLowerCase() === "approved";
        return (
          <div className="admin-tags-cell-wrap">
            {tagsArr.length > 0 ? (
              <>
                <span className="admin-tags-pills">
                  {tagsArr.map((t) => (
                    <span key={t} className="admin-tag-pill">
                      {t}
                    </span>
                  ))}
                </span>
                {isApproved && (
                  <button
                    type="button"
                    className="admin-tags-edit-btn"
                    onClick={() => openTagModal(row)}
                    disabled={approving}
                    aria-label={`Edit tags for ${row.business_name ?? row.email ?? row.id}`}
                  >
                    Edit
                  </button>
                )}
              </>
            ) : isApproved ? (
              <button
                type="button"
                className="admin-tags-add"
                onClick={() => openTagModal(row)}
                disabled={approving}
                aria-label={`Add tags for ${row.business_name ?? row.email ?? row.id}`}
              >
                Add Tag
              </button>
            ) : (
              "—"
            )}
          </div>
        );
      },
    },
    {
      key: "top_rank",
      title: "Top Rank",
      render: (row) => {
        const isApproved = (row.status || "").toLowerCase() === "approved";
        if (!isApproved) return "—";
        const currentRank =
          row.top_rank != null && row.top_rank !== ""
            ? Number(row.top_rank)
            : null;
        const displayValue =
          rankDraft[row.id] !== undefined
            ? rankDraft[row.id]
            : currentRank != null
              ? String(currentRank)
              : "";
        const takenByOthers = list
          .filter(
            (b) =>
              (b.status || "").toLowerCase() === "approved" &&
              String(b.id) !== String(row.id) &&
              normalizeCity(b.city) === normalizeCity(row.city) &&
              b.top_rank != null &&
              b.top_rank !== ""
          )
          .map((b) => Number(b.top_rank));

        const handleRankBlur = () => {
          const raw = displayValue.trim();
          if (raw === "") {
            setRankDraft((prev) => {
              const next = { ...prev };
              delete next[row.id];
              return next;
            });
            if (currentRank != null) {
              dispatch(
                onBusinessUpdate({
                  id: row.id,
                  business_type: row.business_type,
                  top_rank: null,
                })
              );
            }
            return;
          }
          const num = parseInt(raw, 10);
          if (Number.isNaN(num) || num < 1) {
            toast.error("Enter a positive integer.");
            setRankDraft((prev) => {
              const next = { ...prev };
              next[row.id] = currentRank != null ? String(currentRank) : "";
              return next;
            });
            return;
          }
          if (takenByOthers.includes(num)) {
            toast.error("This rank is already in use for this city.");
            setRankDraft((prev) => {
              const next = { ...prev };
              next[row.id] = currentRank != null ? String(currentRank) : "";
              return next;
            });
            return;
          }
          setRankDraft((prev) => {
            const next = { ...prev };
            delete next[row.id];
            return next;
          });
          dispatch(
            onBusinessUpdate({
              id: row.id,
              business_type: row.business_type,
              top_rank: num,
            })
          );
        };

        return (
          <input
            type="number"
            min={1}
            step={1}
            className="admin-table-status-select admin-top-rank-input"
            value={displayValue}
            onChange={(e) => {
              const val = e.target.value;
              setRankDraft((prev) => ({ ...prev, [row.id]: val }));
            }}
            onBlur={handleRankBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
            }}
            disabled={approving}
            placeholder="Rank"
            aria-label={`Top rank for ${row.business_name ?? row.email ?? row.id}`}
          />
        );
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => {
        const isApproved = (row.status || "").toLowerCase() === "approved";
        const canApprove =
          row.profile_step === 4 &&
          (row.status || "").toLowerCase() === "submitted";

        return (
          <div className="admin-table-actions admin-business-actions">
      {isApproved && (
        <button
          type="button"
          className="admin-btn-sm admin-btn-edit"
          onClick={() => openEditModal(row)}
          disabled={approving}
          aria-label={`Edit ${row.business_name ?? row.email ?? row.id}`}
        >
          Edit
        </button>
      )}
            {canApprove && (
              <button
                type="button"
                disabled={row?.is_visible}
                className={
                  row?.is_visible ? "admin-btn-approved" : "admin-btn-approve"
                }
                onClick={() => openApproveModalHandler(row)}
              >
                {row?.is_visible ? "Approved" : "Approve"}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-business-users">
      <div className="admin-page-header-row">
        <h1 className="admin-page-title">Business Users</h1>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={() => navigate("/admin/create-business-user")}
        >
          Create Business User
        </button>
      </div>
      <CommonTable
        columns={columns}
        data={list}
        loading={loading || approving}
        showSearch
        searchPlaceholder="Search all..."
        searchKeys={["business_name", "email", "city", "business_type", "tags", "status"]}
        showCityFilter
        cityKey="city"
      />

      {approveModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeApproveModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="approve-modal-title"
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="approve-modal-title" className="admin-modal-title">
              Confirm approval
            </h2>
            <p className="admin-modal-text">
              Are you sure you want to approve{" "}
              <strong>{approveModal.name}</strong>? This action will mark the
              business as approved.
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeApproveModal}
                disabled={approving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-reject"
                onClick={() => handleConfirmApprove(true)}
                disabled={reject.current}
              >
                {reject.current ? "Rejecting…" : "Reject"}
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-confirm"
                onClick={() => handleConfirmApprove(false)}
                disabled={approve.current}
              >
                {approve.current ? "Approving…" : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tagModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeTagModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="business-tag-modal-title"
        >
          <div className="admin-modal admin-modal-tags" onClick={(e) => e.stopPropagation()}>
            <h2 id="business-tag-modal-title" className="admin-modal-title">
              Edit tags
            </h2>
            <p className="admin-modal-text">
              {tagModal.business_name ?? tagModal.email ?? `Business #${tagModal.id}`}
            </p>
            <div className="admin-modal-form-group">
              <label htmlFor="business-tags-input" className="admin-modal-label">
                Tags (comma-separated or click below)
              </label>
              <div className="admin-tag-draft-chips">
                {parseTags(tagDraft).map((t) => (
                  <span key={t} className="admin-tag-pill admin-tag-pill-removable">
                    {t}
                    <button
                      type="button"
                      className="admin-tag-pill-remove"
                      onClick={() => removeTagFromDraft(t)}
                      disabled={approving}
                      aria-label={`Remove ${t}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <input
                id="business-tags-input"
                type="text"
                className="admin-form-input admin-modal-input"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                placeholder="e.g. Premium, Verified, Featured"
                disabled={approving}
                autoFocus
              />
            </div>
            <div className="admin-tag-suggestions-wrap">
              <span className="admin-tag-suggestions-label">Click to add:</span>
              <div className="admin-tag-suggestions">
                {TAG_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="admin-tag-suggestion-item"
                    onClick={() => addTagToDraft(s)}
                    disabled={approving}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={clearAllTags}
                disabled={approving || !parseTags(tagDraft).length}
              >
                Clear all
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeTagModal}
                disabled={approving}
              >
                Close
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-confirm"
                onClick={handleTagSave}
                disabled={approving}
              >
                {approving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeEditModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-business-modal-title"
        >
          <div className="admin-modal admin-modal--wide admin-modal-edit-business" onClick={(e) => e.stopPropagation()}>
            <h2 id="edit-business-modal-title" className="admin-modal-title">
              Edit business
            </h2>
            <p className="admin-modal-text">
              {editModal.business_name ?? editModal.email ?? `Business #${editModal.id ?? editModal.business_id}`} · {capsFirstLetter(editModal.business_type || "")}
            </p>
            <div className="admin-modal-form admin-modal-form-scroll">
              <section className="admin-edit-section">
                <h3 className="admin-edit-section-title">Business details</h3>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-name">Business name</label>
                    <input
                      id="edit-business-name"
                      type="text"
                      className="admin-modal-input"
                      value={editForm.business_name}
                      onChange={(e) => setEditFormField("business_name", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-email">Email</label>
                    <input
                      id="edit-business-email"
                      type="email"
                      className="admin-modal-input"
                      value={editForm.email}
                      onChange={(e) => setEditFormField("email", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                </div>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-phone">Phone</label>
                    <input
                      id="edit-business-phone"
                      type="text"
                      className="admin-modal-input"
                      value={editForm.phone}
                      onChange={(e) => setEditFormField("phone", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-city">City</label>
                    <input
                      id="edit-business-city"
                      type="text"
                      className="admin-modal-input"
                      value={editForm.city}
                      onChange={(e) => setEditFormField("city", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                </div>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-price-min">Price min (₹)</label>
                    <input
                      id="edit-price-min"
                      type="number"
                      min={0}
                      className="admin-modal-input"
                      value={editForm.price_min}
                      onChange={(e) => setEditFormField("price_min", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-price-max">Price max (₹)</label>
                    <input
                      id="edit-price-max"
                      type="number"
                      min={0}
                      className="admin-modal-input"
                      value={editForm.price_max}
                      onChange={(e) => setEditFormField("price_max", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                </div>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-status">Status</label>
                    <select
                      id="edit-business-status"
                      className="admin-modal-input"
                      value={editForm.status}
                      onChange={(e) => setEditFormField("status", e.target.value)}
                      disabled={approving}
                    >
                      <option value="pending">Pending</option>
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-profile-step">Profile step</label>
                    <select
                      id="edit-profile-step"
                      className="admin-modal-input"
                      value={editForm.profile_step}
                      onChange={(e) => setEditFormField("profile_step", Number(e.target.value))}
                      disabled={approving}
                    >
                      {[1, 2, 3, 4].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label admin-modal-label-checkbox">
                      <input
                        type="checkbox"
                        checked={editForm.is_visible}
                        onChange={(e) => setEditFormField("is_visible", e.target.checked)}
                        disabled={approving}
                      />
                      <span>Visible on site</span>
                    </label>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label admin-modal-label-checkbox">
                      <input
                        type="checkbox"
                        checked={editForm.apply_offers}
                        onChange={(e) => setEditFormField("apply_offers", e.target.checked)}
                        disabled={approving}
                      />
                      <span>Apply offers</span>
                    </label>
                  </div>
                </div>
                <div className="admin-modal-form-row">
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-tags">Tags (comma-separated)</label>
                    <input
                      id="edit-business-tags"
                      type="text"
                      className="admin-modal-input"
                      value={editForm.tags}
                      onChange={(e) => setEditFormField("tags", e.target.value)}
                      placeholder="e.g. Premium, Verified, Featured"
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-business-top-rank">Top rank</label>
                    <input
                      id="edit-business-top-rank"
                      type="number"
                      min={1}
                      step={1}
                      className="admin-modal-input"
                      value={editForm.top_rank}
                      onChange={(e) => setEditFormField("top_rank", e.target.value)}
                      placeholder="Leave empty for no rank"
                      disabled={approving}
                    />
                  </div>
                </div>
              </section>

              {editModal.business_type === "venue" && editModal.venue && (
                <section className="admin-edit-section">
                  <h3 className="admin-edit-section-title">Venue details</h3>
                  <div className="admin-modal-form-row">
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-venue-type">Venue type</label>
                      <input
                        id="edit-venue-type"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.venue_type}
                        onChange={(e) => setEditFormField("venue_type", e.target.value)}
                        placeholder="e.g. banquet, lawn"
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-capacity-min">Capacity min</label>
                      <input
                        id="edit-capacity-min"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.capacity_min}
                        onChange={(e) => setEditFormField("capacity_min", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-capacity-max">Capacity max</label>
                      <input
                        id="edit-capacity-max"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.capacity_max}
                        onChange={(e) => setEditFormField("capacity_max", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                  </div>
                  <div className="admin-modal-form-row">
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-area-sqft">Area (sq ft)</label>
                      <input
                        id="edit-area-sqft"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.area_sqft}
                        onChange={(e) => setEditFormField("area_sqft", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-number-of-halls">Number of halls</label>
                      <input
                        id="edit-number-of-halls"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.number_of_halls}
                        onChange={(e) => setEditFormField("number_of_halls", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-full-address">Full address</label>
                    <textarea
                      id="edit-full-address"
                      className="admin-modal-input admin-modal-textarea"
                      rows={2}
                      value={editForm.full_address}
                      onChange={(e) => setEditFormField("full_address", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-row">
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-venue-city">City</label>
                      <input
                        id="edit-venue-city"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.venue_city}
                        onChange={(e) => setEditFormField("venue_city", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-state">State</label>
                      <input
                        id="edit-state"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.state}
                        onChange={(e) => setEditFormField("state", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-pincode">Pincode</label>
                      <input
                        id="edit-pincode"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.pincode}
                        onChange={(e) => setEditFormField("pincode", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-description">Description</label>
                    <textarea
                      id="edit-description"
                      className="admin-modal-input admin-modal-textarea"
                      rows={4}
                      value={editForm.description}
                      onChange={(e) => setEditFormField("description", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <AmenitiesChipInput
                      label="Amenities"
                      value={editForm.amenities}
                      onChange={(e) => setEditFormField("amenities", e.target.value)}
                      placeholder="Type or pick from suggestions below"
                      disabled={approving}
                      readOnly={approving}
                    />
                  </div>
                </section>
              )}

              {editModal.business_type === "vendor" && (
                <section className="admin-edit-section">
                  <h3 className="admin-edit-section-title">Vendor details</h3>
                  <div className="admin-modal-form-row">
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-vendor-type">Vendor type</label>
                      <input
                        id="edit-vendor-type"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.vendor_type}
                        onChange={(e) => setEditFormField("vendor_type", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-willing-to-travel">Willing to travel</label>
                      <input
                        id="edit-willing-to-travel"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.willing_to_travel}
                        onChange={(e) => setEditFormField("willing_to_travel", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-years-experience">Years of experience</label>
                      <input
                        id="edit-years-experience"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.years_of_experience}
                        onChange={(e) => setEditFormField("years_of_experience", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-service-radius">Service radius (km)</label>
                      <input
                        id="edit-service-radius"
                        type="number"
                        min={0}
                        className="admin-modal-input"
                        value={editForm.service_radius_km}
                        onChange={(e) => setEditFormField("service_radius_km", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-vendor-full-address">Full address</label>
                    <textarea
                      id="edit-vendor-full-address"
                      className="admin-modal-input admin-modal-textarea"
                      rows={2}
                      value={editForm.full_address}
                      onChange={(e) => setEditFormField("full_address", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-row">
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-vendor-venue-city">City</label>
                      <input
                        id="edit-vendor-venue-city"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.venue_city}
                        onChange={(e) => setEditFormField("venue_city", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-vendor-state">State</label>
                      <input
                        id="edit-vendor-state"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.state}
                        onChange={(e) => setEditFormField("state", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                    <div className="admin-modal-form-group">
                      <label className="admin-modal-label" htmlFor="edit-vendor-pincode">Pincode</label>
                      <input
                        id="edit-vendor-pincode"
                        type="text"
                        className="admin-modal-input"
                        value={editForm.pincode}
                        onChange={(e) => setEditFormField("pincode", e.target.value)}
                        disabled={approving}
                      />
                    </div>
                  </div>
                  <div className="admin-modal-form-group">
                    <label className="admin-modal-label" htmlFor="edit-vendor-description">Description</label>
                    <textarea
                      id="edit-vendor-description"
                      className="admin-modal-input admin-modal-textarea"
                      rows={4}
                      value={editForm.description}
                      onChange={(e) => setEditFormField("description", e.target.value)}
                      disabled={approving}
                    />
                  </div>
                  <div className="admin-modal-form-group">
                    <AmenitiesChipInput
                      label="Offerings"
                      value={editForm.amenities}
                      onChange={(e) => setEditFormField("amenities", e.target.value)}
                      placeholder="Type or pick from suggestions below"
                      readOnly={approving}
                      suggestions={APP_CONFIG.VENDOR_OFFERING_TAGS?.[editForm.vendor_type] || []}
                    />
                  </div>
                </section>
              )}

              <section className="admin-edit-section">
                <h3 className="admin-edit-section-title">Images</h3>
                {imageData?.getImagesLoading && imagesForBusinessIdRef.current === (editModal?.id ?? editModal?.business_id) && editModalImages().length === 0 ? (
                  <p className="admin-edit-images-loading">Loading images…</p>
                ) : (
                  <>
                    <div className="admin-edit-images-grid">
                      {editModalImages().map((img) => (
                        <div key={img.id} className="admin-edit-image-card">
                          <img
                            src={img.image_url || img.url || img.path}
                            alt={img.image_type === "cover" ? "Cover" : `Gallery ${img.display_order ?? ""}`}
                            className="admin-edit-image-thumb"
                          />
                          <span className={`admin-edit-image-badge admin-edit-image-badge-${(img.image_type || img.type || "gallery").toLowerCase()}`}>
                            {img.image_type === "cover" || img.type === "cover" ? "Cover" : "Gallery"}
                          </span>
                          <button
                            type="button"
                            className="admin-edit-image-delete"
                            onClick={() => handleDeleteImage(img.id)}
                            disabled={imageData?.singleImageLoading}
                            aria-label={`Delete image ${img.id}`}
                            title="Delete image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="admin-edit-images-actions">
                      <input
                        ref={editImageInputRef}
                        type="file"
                        accept="image/*"
                        className="admin-edit-image-input-hidden"
                        onChange={handleAddImage}
                        aria-label="Upload image"
                      />
                      <button
                        type="button"
                        className="admin-modal-btn admin-btn-sm admin-btn-edit"
                        onClick={() => editImageInputRef.current?.click()}
                        disabled={imageData?.singleImageLoading}
                      >
                        {imageData?.singleImageLoading ? "Uploading…" : "+ Add more images"}
                      </button>
                    </div>
                  </>
                )}
              </section>

              <div className="admin-modal-actions admin-modal-actions-sticky">
                <button
                  type="button"
                  className="admin-modal-btn admin-modal-cancel"
                  onClick={closeEditModal}
                  disabled={approving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-modal-btn admin-modal-confirm"
                  onClick={handleEditSave}
                  disabled={approving || !isEditFormChanged()}
                >
                  {approving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessUsersPage;
